import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Store, Phone, MapPin, Clock, Truck, CheckCircle2, 
  ArrowRight, ArrowLeft, Loader2, RefreshCw, AlertCircle,
  Building2, User, Mail, MessageCircle, Calendar, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiConfig';

// Step indicator component
function StepIndicator({ currentStep, totalSteps = 4 }) {
  const steps = [
    { num: 1, label: 'Información Básica' },
    { num: 2, label: 'Verificación' },
    { num: 3, label: 'Detalles del Negocio' },
    { num: 4, label: 'Confirmación' }
  ];
  
  return (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="registration-step-indicator">
      {steps.map((step, i) => (
        <div key={step.num} className="flex items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              i < currentStep 
                ? 'bg-emerald-600 text-white' 
                : i === currentStep 
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-200 text-slate-500'
            }`}
            data-testid={`registration-step-${step.num}`}
          >
            {i < currentStep ? <CheckCircle2 className="w-5 h-5" /> : step.num}
          </div>
          {i < steps.length - 1 && (
            <div className={`w-8 h-1 mx-1 rounded ${i < currentStep ? 'bg-emerald-600' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function MerchantRegistration() {
  // Form state
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);
  const [pilotCode, setPilotCode] = useState(null);
  
  // Step 1: Basic info
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  
  // Step 2: Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Step 3: Business details
  const [businessType, setBusinessType] = useState('colmado');
  const [hasDelivery, setHasDelivery] = useState(true);
  const [deliveryRadius, setDeliveryRadius] = useState('');
  const [openingTime, setOpeningTime] = useState('07:00');
  const [closingTime, setClosingTime] = useState('21:00');
  const [daysOpen, setDaysOpen] = useState('Lunes a Sábado');
  const [streetAddress, setStreetAddress] = useState('');
  const [landmark, setLandmark] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [estimatedTransactions, setEstimatedTransactions] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [notes, setNotes] = useState('');
  
  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Step 1: Start application
  const handleStartApplication = async () => {
    if (!businessName || !ownerName || !phone || !city) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/merchant/register/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: businessName,
          owner_name: ownerName,
          phone: phone,
          email: email || null,
          location_city: city,
          location_neighborhood: neighborhood || null,
          whatsapp_number: whatsapp || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setApplicationId(data.application_id);
        if (data._pilot_verification_code) {
          setPilotCode(data._pilot_verification_code);
        }
        setStep(1);
        toast.success('¡Código de verificación enviado!');
      } else {
        toast.error(data.error || 'Error al iniciar solicitud');
        if (data.existing_application_id) {
          setApplicationId(data.existing_application_id);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify phone
  const handleVerifyPhone = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Ingrese el código de 6 dígitos');
      return;
    }
    
    setLoading(true);
    setVerificationError('');
    
    try {
      const response = await fetch(`${API_URL}/api/merchant/register/verify-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationId,
          verification_code: verificationCode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVerificationToken(data.phone_verified_token);
        setStep(2);
        toast.success('¡Teléfono verificado!');
      } else {
        setVerificationError(data.error || 'Código incorrecto');
        if (data.attempts_remaining !== undefined) {
          setAttemptsRemaining(data.attempts_remaining);
        }
        if (data._pilot_verification_code) {
          setPilotCode(data._pilot_verification_code);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/merchant/register/resend-code?application_id=${applicationId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Nuevo código enviado');
        setResendCooldown(60);
        setVerificationCode('');
        setAttemptsRemaining(5);
        if (data._pilot_verification_code) {
          setPilotCode(data._pilot_verification_code);
        }
      } else {
        toast.error(data.error || 'Error al reenviar código');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Complete application
  const handleCompleteApplication = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/merchant/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationId,
          phone_verified_token: verificationToken,
          business_type: businessType,
          has_delivery: hasDelivery,
          delivery_radius_km: deliveryRadius ? parseFloat(deliveryRadius) : null,
          opening_time: openingTime,
          closing_time: closingTime,
          days_open: daysOpen,
          street_address: streetAddress || null,
          landmark: landmark || null,
          years_in_business: yearsInBusiness ? parseInt(yearsInBusiness) : null,
          estimated_monthly_transactions: estimatedTransactions ? parseInt(estimatedTransactions) : null,
          how_heard_about_us: howHeard || null,
          additional_notes: notes || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStep(3);
        toast.success('¡Solicitud enviada exitosamente!');
      } else {
        toast.error(data.error || 'Error al enviar solicitud');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">PeCash para Negocios</h1>
              <p className="text-sm text-slate-500">Registro de Colmado</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <StepIndicator currentStep={step} />

        <AnimatePresence mode="wait">
          {/* Step 1: Basic Information */}
          {step === 0 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Información Básica</h2>
              <p className="text-slate-500 mb-6">Cuéntenos sobre su colmado</p>

              <div className="space-y-4">
                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Nombre del Colmado *
                  </label>
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Ej: Colmado La Bendición"
                    className="h-12"
                    data-testid="merchant-business-name"
                  />
                </div>

                {/* Owner Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Nombre del Propietario *
                  </label>
                  <Input
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Nombre completo"
                    className="h-12"
                    data-testid="merchant-owner-name"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Teléfono *
                  </label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 809 555 1234"
                    className="h-12"
                    data-testid="merchant-phone"
                  />
                  <p className="text-xs text-slate-500 mt-1">Recibirá un código de verificación</p>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Correo Electrónico (opcional)
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="h-12"
                    data-testid="merchant-email"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Ciudad *
                  </label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Ej: Santo Domingo Este"
                    className="h-12"
                    data-testid="merchant-city"
                  />
                </div>

                {/* Neighborhood */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Barrio/Sector (opcional)
                  </label>
                  <Input
                    value={neighborhood}
                    onChange={(e) => setNeighborhood(e.target.value)}
                    placeholder="Ej: Los Mina"
                    className="h-12"
                    data-testid="merchant-neighborhood"
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <MessageCircle className="w-4 h-4 inline mr-1" />
                    WhatsApp (si es diferente al teléfono)
                  </label>
                  <Input
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+1 809 555 1234"
                    className="h-12"
                    data-testid="merchant-whatsapp"
                  />
                </div>

                {/* Submit */}
                <Button
                  onClick={handleStartApplication}
                  disabled={loading || !businessName || !ownerName || !phone || !city}
                  className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-lg font-semibold rounded-xl mt-4"
                  data-testid="merchant-start-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Continuar
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Verification */}
          {step === 1 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Verificar Teléfono</h2>
              <p className="text-slate-500 mb-6">
                Ingrese el código de 6 dígitos enviado a <strong>***{phone.slice(-4)}</strong>
              </p>

              {/* Pilot mode code display */}
              {pilotCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-amber-800 text-sm font-medium">
                    <Info className="w-4 h-4 inline mr-1" />
                    Modo Piloto - Código: <span className="font-mono font-bold">{pilotCode}</span>
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {/* Verification Code Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Código de Verificación
                  </label>
                  <Input
                    value={verificationCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(val);
                      setVerificationError('');
                    }}
                    placeholder="000000"
                    className="h-14 text-center text-2xl font-mono tracking-widest"
                    maxLength={6}
                    data-testid="merchant-verification-code"
                  />
                  {verificationError && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {verificationError}
                    </p>
                  )}
                  <p className="text-slate-500 text-sm mt-2">
                    Intentos restantes: {attemptsRemaining}
                  </p>
                </div>

                {/* Verify Button */}
                <Button
                  onClick={handleVerifyPhone}
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-lg font-semibold rounded-xl"
                  data-testid="merchant-verify-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      Verificar
                      <CheckCircle2 className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Resend Code */}
                <div className="text-center">
                  <button
                    onClick={handleResendCode}
                    disabled={loading || resendCooldown > 0}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium disabled:text-slate-400"
                  >
                    {resendCooldown > 0 ? (
                      <>Reenviar código en {resendCooldown}s</>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 inline mr-1" />
                        Reenviar código
                      </>
                    )}
                  </button>
                </div>

                {/* Back Button */}
                <Button
                  variant="ghost"
                  onClick={() => setStep(0)}
                  className="w-full text-slate-500"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Business Details */}
          {step === 2 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Detalles del Negocio</h2>
              <p className="text-slate-500 mb-6">Información adicional sobre su colmado</p>

              <div className="space-y-4">
                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Tipo de Negocio
                  </label>
                  <select
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="w-full h-12 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="merchant-business-type"
                  >
                    <option value="colmado">Colmado</option>
                    <option value="supermercado">Supermercado</option>
                    <option value="farmacia">Farmacia</option>
                    <option value="ferreteria">Ferretería</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                {/* Delivery */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hasDelivery"
                    checked={hasDelivery}
                    onChange={(e) => setHasDelivery(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    data-testid="merchant-has-delivery"
                  />
                  <label htmlFor="hasDelivery" className="text-slate-700">
                    <Truck className="w-4 h-4 inline mr-1" />
                    Ofrecemos servicio de entrega a domicilio
                  </label>
                </div>

                {hasDelivery && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Radio de Entrega (km)
                    </label>
                    <Input
                      type="number"
                      value={deliveryRadius}
                      onChange={(e) => setDeliveryRadius(e.target.value)}
                      placeholder="Ej: 5"
                      className="h-12"
                      data-testid="merchant-delivery-radius"
                    />
                  </div>
                )}

                {/* Operating Hours */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Hora de Apertura
                    </label>
                    <Input
                      type="time"
                      value={openingTime}
                      onChange={(e) => setOpeningTime(e.target.value)}
                      className="h-12"
                      data-testid="merchant-opening-time"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Hora de Cierre
                    </label>
                    <Input
                      type="time"
                      value={closingTime}
                      onChange={(e) => setClosingTime(e.target.value)}
                      className="h-12"
                      data-testid="merchant-closing-time"
                    />
                  </div>
                </div>

                {/* Days Open */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Días de Operación
                  </label>
                  <Input
                    value={daysOpen}
                    onChange={(e) => setDaysOpen(e.target.value)}
                    placeholder="Ej: Lunes a Sábado"
                    className="h-12"
                    data-testid="merchant-days-open"
                  />
                </div>

                {/* Street Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Dirección (opcional)
                  </label>
                  <Input
                    value={streetAddress}
                    onChange={(e) => setStreetAddress(e.target.value)}
                    placeholder="Calle y número"
                    className="h-12"
                    data-testid="merchant-street-address"
                  />
                </div>

                {/* Landmark */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Punto de Referencia (opcional)
                  </label>
                  <Input
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                    placeholder="Ej: Al lado de la iglesia"
                    className="h-12"
                    data-testid="merchant-landmark"
                  />
                </div>

                {/* Years in Business */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Años en Operación (opcional)
                  </label>
                  <Input
                    type="number"
                    value={yearsInBusiness}
                    onChange={(e) => setYearsInBusiness(e.target.value)}
                    placeholder="Ej: 5"
                    className="h-12"
                    data-testid="merchant-years"
                  />
                </div>

                {/* How Heard */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ¿Cómo escuchó de PeCash? (opcional)
                  </label>
                  <Input
                    value={howHeard}
                    onChange={(e) => setHowHeard(e.target.value)}
                    placeholder="Ej: Facebook, un amigo, etc."
                    className="h-12"
                    data-testid="merchant-how-heard"
                  />
                </div>

                {/* Submit */}
                <Button
                  onClick={handleCompleteApplication}
                  disabled={loading}
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-semibold rounded-xl mt-4"
                  data-testid="merchant-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Solicitud
                      <CheckCircle2 className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Back Button */}
                <Button
                  variant="ghost"
                  onClick={() => setStep(1)}
                  className="w-full text-slate-500"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Success */}
          {step === 3 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-800 mb-2">¡Solicitud Enviada!</h2>
              <p className="text-slate-500 mb-6">
                Un representante de PeCash se pondrá en contacto con usted en las próximas 24-48 horas.
              </p>

              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-600 mb-1">Número de Referencia</p>
                <p className="text-lg font-mono font-bold text-purple-600" data-testid="merchant-reference-number">
                  {applicationId}
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 text-left">
                <h3 className="font-semibold text-purple-800 mb-2">Próximos Pasos:</h3>
                <ol className="text-sm text-purple-700 space-y-2">
                  <li>1. Revisaremos su solicitud</li>
                  <li>2. Le contactaremos para verificar información</li>
                  <li>3. Una vez aprobado, le configuraremos su cuenta</li>
                  <li>4. ¡Listo para recibir pagos PeCash!</li>
                </ol>
              </div>

              <Button
                onClick={() => window.location.href = '/'}
                className="w-full h-12 mt-6 bg-purple-600 hover:bg-purple-700"
              >
                Volver al Inicio
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="max-w-2xl mx-auto px-6 py-8 text-center">
        <p className="text-sm text-slate-500">
          ¿Necesita ayuda? Contáctenos al <a href="tel:+18095551234" className="text-purple-600">+1 809 555 1234</a>
        </p>
      </footer>
    </div>
  );
}
